import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Send } from "lucide-react";
import { api } from "../lib/api";

const emptyPromo = { title: "", description: "", start_date: "", end_date: "", conditions: "", status: "active" };

export default function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [notif, setNotif] = useState({ title: "", message: "" });
  const [notifications, setNotifications] = useState([]);

  const load = () => {
    api.get("/admin/promotions").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setNotifications(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const savePromo = async (e) => {
    e.preventDefault();
    const payload = { ...editing, start_date: editing.start_date || null, end_date: editing.end_date || null };
    try {
      if (editing.id) await api.put(`/admin/promotions/${editing.id}`, payload);
      else await api.post("/admin/promotions", payload);
      toast.success("Promotion enregistrée");
      setEditing(null);
      load();
    } catch { toast.error("Erreur"); }
  };

  const removePromo = async (id) => {
    if (!window.confirm("Supprimer cette promotion ?")) return;
    try { await api.delete(`/admin/promotions/${id}`); load(); } catch { toast.error("Erreur"); }
  };

  const sendNotif = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/notifications", notif);
      toast.success("Notification envoyée à tous les clients");
      setNotif({ title: "", message: "" });
      load();
    } catch { toast.error("Erreur"); }
  };

  const removeNotif = async (id) => {
    try { await api.delete(`/admin/notifications/${id}`); load(); } catch { toast.error("Erreur"); }
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  return (
    <div data-testid="admin-promotions">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-elegant text-3xl font-bold">Promotions &amp; Notifications</h1>
        <button data-testid="promo-add-btn" onClick={() => setEditing({ ...emptyPromo })} className="btn-gold !py-2 text-sm"><Plus size={16} /> Nouvelle promotion</button>
      </div>

      {editing && (
        <form onSubmit={savePromo} className="ic-card !transform-none p-6 mb-8 space-y-4" data-testid="promo-edit-form">
          <input data-testid="promo-form-title" required className="ic-input" placeholder="Titre de la promotion *" value={editing.title} onChange={set("title")} />
          <textarea data-testid="promo-form-description" rows="2" className="ic-input" placeholder="Description" value={editing.description} onChange={set("description")} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <input data-testid="promo-form-start" type="date" className="ic-input" value={editing.start_date || ""} onChange={set("start_date")} />
            <input data-testid="promo-form-end" type="date" className="ic-input" value={editing.end_date || ""} onChange={set("end_date")} />
            <select data-testid="promo-form-status" className="ic-input" value={editing.status} onChange={set("status")}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <input data-testid="promo-form-conditions" className="ic-input" placeholder="Conditions" value={editing.conditions} onChange={set("conditions")} />
          <div className="flex gap-3">
            <button type="submit" data-testid="promo-form-save" className="btn-gold !py-2 text-sm">Enregistrer</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold !py-2 text-sm">Annuler</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="gold-label mb-4">Promotions</h2>
          <div className="space-y-3">
            {promos.map((p) => (
              <div key={p.id} className="ic-card !transform-none p-5" data-testid={`promo-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-medium">{p.title}</p>
                  <span className={`text-xs border rounded-full px-3 py-1 ${p.status === "active" ? "text-[#009246] border-[#009246]/40" : "text-gray-400 border-gray-600"}`}>
                    {p.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="text-sm text-gray-400 mt-2">{p.description}</p>
                {p.conditions && <p className="text-xs text-gray-500 mt-1">Conditions : {p.conditions}</p>}
                <div className="flex gap-2 mt-3">
                  <button data-testid={`promo-edit-${p.id}`} onClick={() => setEditing({ ...p })} className="p-1.5 text-gray-400 hover:text-[#D4AF37]"><Pencil size={15} /></button>
                  <button data-testid={`promo-delete-${p.id}`} onClick={() => removePromo(p.id)} className="p-1.5 text-gray-400 hover:text-[#ff6b81]"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="gold-label mb-4">Envoyer une notification aux clients</h2>
          <form onSubmit={sendNotif} className="ic-card !transform-none p-6 space-y-4 mb-6" data-testid="notif-form">
            <input data-testid="notif-form-title" required className="ic-input" placeholder="Titre (ex: Soirée Karaoké samedi !) *" value={notif.title} onChange={(e) => setNotif({ ...notif, title: e.target.value })} />
            <textarea data-testid="notif-form-message" required rows="3" className="ic-input" placeholder="Message *" value={notif.message} onChange={(e) => setNotif({ ...notif, message: e.target.value })} />
            <button type="submit" data-testid="notif-send-btn" className="btn-gold !py-2 text-sm"><Send size={15} /> Envoyer</button>
          </form>
          <div className="space-y-2">
            {notifications.map((n) => (
              <div key={n.id} className="ic-card !transform-none p-4 flex justify-between items-start gap-3">
                <div>
                  <p className="text-sm font-medium">{n.title}</p>
                  <p className="text-xs text-gray-400 mt-1">{n.message}</p>
                </div>
                <button onClick={() => removeNotif(n.id)} className="p-1.5 text-gray-500 hover:text-[#ff6b81]"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
